import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

import { userAPI } from '../UserAPI';
import { authenticationAtom } from '../State';
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from '@/hooks/useCustomerLogo';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

const LOGGING_OUT = 'Logging out...';

const isAllowedReturnTo = (value: string | null) => {
  if (!value) return false;
  if (value.startsWith('http://') || value.startsWith('https://')) return true;
  if (value.startsWith('/')) return true;
  return false;
};

export const LogoutPage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(LOGGING_OUT);
  const { logo, isFallback } = useCustomerLogo();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const returnTo = params.get('returnTo');
  const [authentication] = useAtom(authenticationAtom) as any;
  // Tenant to keep after logout: explicit ?tenant=<id> wins, else the logged-in
  // user's tenant (captured before logout clears it).
  const tenant = params.get('tenant') || authentication?.customer;

  useEffect(() => {
    const run = async () => {
      try {
        await userAPI.logout();
      } catch (error) {
        console.warn('Logout failed:', error);
      }

      if (isAllowedReturnTo(returnTo)) {
        window.location.assign(returnTo as string);
        return;
      }

      setStatus('Logged out.');
      navigate(tenant ? `/login?tenant=${encodeURIComponent(tenant)}` : '/login');
    };

    run();
  }, [navigate, returnTo, tenant]);

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-background p-4">
      <img
        src={logo}
        alt={isFallback ? BRAND.name : ''}
        className="h-16 w-auto max-w-[240px] object-contain"
      />
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-lg font-semibold leading-none tracking-tight">Logout</h1>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          {status === LOGGING_OUT && (
            <Loader2
              aria-hidden="true"
              className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
            />
          )}
          <p role="status" className="min-w-0 text-sm text-muted-foreground">
            {status}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
